import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProjectForm from '../../components/Admin/ProjectForm';
import ProjectList from '../../components/Admin/ProjectList';
import DeleteConfirmModal from '../../components/Admin/DeleteConfirmModal';
import './AdminDashboard.css';

const API_URL = import.meta.env.VITE_API_URL || '';

const CATEGORIES = ['BRANDING', 'Marketing', 'Advertising'];

/** POST-only route avoids Vercel/edge 405 + non-JSON body on DELETE to /api/projects/[id]. */
async function deleteProjectRequest(projectId, token) {
  return fetch(`${API_URL}/api/delete-project`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ id: String(projectId) }),
  });
}

/** API must return JSON; if you see HTML (e.g. index.html), the request missed the serverless route. */
async function parseApiJson(response) {
  const text = await response.text();
  const trimmed = text.trim();
  if (trimmed.startsWith('<')) {
    throw new Error(
      'API returned a web page instead of JSON. Check VITE_API_URL (same host as the admin) and that /api is deployed.'
    );
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    throw new Error(`Invalid response (${response.status}). Expected JSON from the API.`);
  }
}

const AdminDashboard = () => {
  const [projectsByCategory, setProjectsByCategory] = useState({
    BRANDING: [],
    Marketing: [],
    Advertising: []
  });
  const [activeCategory, setActiveCategory] = useState('BRANDING');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    verifyAuth();
    fetchProjects();
  }, []);

  const verifyAuth = async () => {
    const token = localStorage.getItem('adminToken');
    const userData = localStorage.getItem('adminUser');

    if (!token) {
      navigate('/admin');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Invalid token');
      }

      setUser(JSON.parse(userData));
    } catch (error) {
      console.error('Auth verification failed:', error);
      handleLogout();
    }
  };

  const fetchProjects = async (options = {}) => {
    const { silent = false } = options;
    if (!silent) setLoading(true);
    try {
      // Admin must bypass HTTP cache — GET /api/projects sends s-maxage=60 so the list would stay stale after save
      const response = await fetch(`${API_URL}/api/projects`, { cache: 'no-store' });
      const data = await response.json();

      if (data.success) {
        setProjectsByCategory(data.data);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleMoveProject = async (projectId, direction) => {
    const projects = [...projectsByCategory[activeCategory]];
    const currentIndex = projects.findIndex(p => p._id === projectId);
    
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= projects.length) return;
    
    // Swap projects
    [projects[currentIndex], projects[newIndex]] = [projects[newIndex], projects[currentIndex]];
    
    // Update local state immediately for responsive UI
    setProjectsByCategory(prev => ({
      ...prev,
      [activeCategory]: projects
    }));
    
    // Prepare batch update with new sort orders
    const updates = projects.map((project, index) => ({
      id: project._id,
      sortOrder: index
    }));
    
    // Send batch update to server
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API_URL}/api/projects/reorder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ updates })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update order');
      }
    } catch (error) {
      console.error('Error reordering projects:', error);
      // Revert on error
      fetchProjects();
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin');
  };

  const handleAddProject = () => {
    setEditingProject(null);
    setShowForm(true);
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setShowForm(true);
  };

  const handleDeleteProject = async (projectId) => {
    // Step 1: Simple confirmation
    if (!window.confirm('Are you sure you want to delete this project? This will also delete all associated media files.')) {
      return;
    }

    // Step 2: Find project and show auth modal
    let projectToDelete = null;
    Object.values(projectsByCategory).forEach(projects => {
      const found = projects.find(p => p._id === projectId);
      if (found) {
        projectToDelete = found;
      }
    });

    if (!projectToDelete) {
      alert('Project not found');
      return;
    }

    setProjectToDelete(projectToDelete);
  };

  const confirmDeleteWithAuth = async () => {
    if (!projectToDelete) return;

    const projectId = projectToDelete._id;
    const token = localStorage.getItem('adminToken');
    const projectSnapshot = projectToDelete;

    // Store the original state for rollback if needed
    const originalState = { ...projectsByCategory };
    
    // Close modal
    setProjectToDelete(null);

    // Optimistically remove from UI immediately
    setProjectsByCategory(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(category => {
        updated[category] = updated[category].filter(p => p._id !== projectId);
      });
      return updated;
    });

    try {
      const response = await deleteProjectRequest(projectId, token);

      const data = await parseApiJson(response);

      if (!data.success) {
        // Revert on failure
        setProjectsByCategory(originalState);
        alert('Failed to delete project');
        return;
      }

      // Media URLs from list payload (same fields as GET list) — avoids extra GET that can return HTML if /api is misrouted
      if (projectSnapshot) {
        const project = projectSnapshot;
        const mediaUrls = [];

        // Add main media
        if (project.media) mediaUrls.push(project.media);
        if (project.thumbnail) mediaUrls.push(project.thumbnail);

        // Add section media
        if (project.projectContent?.sections) {
          project.projectContent.sections.forEach(section => {
            if (section.media) mediaUrls.push(section.media);
            if (section.mobileMedia) mediaUrls.push(section.mobileMedia);
            if (section.imageName) mediaUrls.push(section.imageName);
          });
        }

        // Delete all blob files (non-blocking)
        if (mediaUrls.length > 0) {
          fetch(`${API_URL}/api/blob/delete`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ urls: mediaUrls })
          }).catch(err => {
            console.warn('Failed to delete some blob files:', err);
            // Don't show error to user, project is already deleted
          });
        }
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      setProjectsByCategory(originalState);
      alert(error?.message || 'Error deleting project');
    }
  };

  const handleFormSubmit = (savedProject) => {
    setShowForm(false);
    setEditingProject(null);
    if (savedProject?._id) {
      setProjectsByCategory((prev) => {
        const next = { BRANDING: [], Marketing: [], Advertising: [] };
        const newCat = savedProject.category || 'BRANDING';
        for (const cat of ['BRANDING', 'Marketing', 'Advertising']) {
          const list = prev[cat] || [];
          const without = list.filter((p) => p._id !== savedProject._id);
          if (cat === newCat) {
            next[cat] = [...without, savedProject].sort(
              (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
            );
          } else {
            next[cat] = without;
          }
        }
        return next;
      });
    }
    fetchProjects({ silent: true });
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingProject(null);
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-loading">Loading...</div>
      </div>
    );
  }

  const currentProjects = projectsByCategory[activeCategory] || [];
  const totalProjects = Object.values(projectsByCategory).reduce((sum, arr) => sum + arr.length, 0);

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-content">
          <div>
            <h1>VZBL CMS Dashboard</h1>
            {user && (
              <p className="admin-user-info">
                Welcome, <strong>{user.username}</strong> ({user.role})
              </p>
            )}
          </div>
          <button onClick={handleLogout} className="admin-logout-btn">
            Logout
          </button>
        </div>
      </header>

      <div className="admin-content">
        {/* Category Tabs */}
        <div className="category-tabs">
          {CATEGORIES.map(category => (
            <button
              key={category}
              className={`category-tab ${activeCategory === category ? 'active' : ''}`}
              onClick={() => setActiveCategory(category)}
            >
              {category}
              <span className="tab-count">
                {projectsByCategory[category]?.length || 0}
              </span>
            </button>
          ))}
        </div>

        <div className="admin-toolbar">
          <h2>
            {activeCategory} Projects ({currentProjects.length})
            <span className="total-count">Total: {totalProjects}</span>
          </h2>
          <button onClick={handleAddProject} className="admin-add-btn">
            + Add New Project
          </button>
        </div>

        {showForm ? (
          <ProjectForm
            project={editingProject}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
          />
        ) : (
          <ProjectList
            projects={currentProjects}
            onEdit={handleEditProject}
            onDelete={handleDeleteProject}
            onMove={handleMoveProject}
          />
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {projectToDelete && (
        <DeleteConfirmModal
          project={projectToDelete}
          onConfirm={confirmDeleteWithAuth}
          onCancel={() => setProjectToDelete(null)}
        />
      )}
    </div>
  );
};

export default AdminDashboard;

